/**
 * Mapper App — TypeScript interfaces and constants.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Map, Layers, MapPin, Route, Pentagon, CircleDot, Sparkles, Upload, PenTool,
  Landmark, Building2, Fence, Factory, Tractor, Mountain, Zap, Truck,
  Droplets, Wheat, CircleDashed, Warehouse, Thermometer, Wind,
  Leaf, Grid3x3, ShieldAlert,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Core types                                                          */
/* ------------------------------------------------------------------ */

export type LayerType = 'boundary' | 'infrastructure' | 'parcel' | 'import' | 'agent' | 'custom';
export type FeatureType = 'Point' | 'Polygon' | 'LineString' | 'MultiPolygon' | 'MultiLineString' | 'MultiPoint';
export type FeatureStatus = 'active' | 'draft' | 'review' | 'decommissioned';
export type FeatureSource = 'manual' | 'import:geojson' | 'import:kml' | 'import:kmz' | 'import:shp' | 'agent' | 'agent:osm' | 'agent:gee' | 'agent:vision';
export type LandCategory = 'agricultural' | 'urban' | 'industrial' | 'mixed';
export type MapperLayerKey =
  | 'land_use'
  | 'water'
  | 'structures'
  | 'boundaries'
  | 'transport'
  | 'livestock'
  | 'utilities'
  | 'storage'
  | 'reference';

/* ------------------------------------------------------------------ */
/*  Function-based Infrastructure Category types (v2)                   */
/* ------------------------------------------------------------------ */

/** Function-based infrastructure categories */
export type InfraCategoryType =
  | 'natural_environment'
  | 'land_use'
  | 'buildings'
  | 'linear'
  | 'water_infrastructure'
  | 'energy_utilities'
  | 'waste_environmental';

/** @deprecated — kept for backward compat during migration; prefer InfraCategoryType */
export type IndustryType = 'agriculture' | 'mining' | 'energy' | 'logistics' | 'common';

/** Category colors (match taxonomy.json) */
export const CATEGORY_COLORS: Record<InfraCategoryType, { bg: string; text: string; hex: string }> = {
  natural_environment:  { bg: 'bg-green-100',  text: 'text-green-700',  hex: '#22c55e' },
  land_use:             { bg: 'bg-amber-100',  text: 'text-amber-700',  hex: '#a16207' },
  buildings:            { bg: 'bg-red-100',    text: 'text-red-700',    hex: '#dc2626' },
  linear:               { bg: 'bg-slate-100',  text: 'text-slate-700',  hex: '#64748b' },
  water_infrastructure: { bg: 'bg-sky-100',    text: 'text-sky-700',    hex: '#0284c7' },
  energy_utilities:     { bg: 'bg-yellow-100', text: 'text-yellow-700', hex: '#eab308' },
  waste_environmental:  { bg: 'bg-orange-100', text: 'text-orange-700', hex: '#92400e' },
};

/** Category icons */
export const CATEGORY_ICONS: Record<InfraCategoryType, LucideIcon> = {
  natural_environment:  Leaf,
  land_use:             Grid3x3,
  buildings:            Building2,
  linear:               Route,
  water_infrastructure: Droplets,
  energy_utilities:     Zap,
  waste_environmental:  ShieldAlert,
};

/** Category labels for display */
export const CATEGORY_LABELS: Record<InfraCategoryType, string> = {
  natural_environment:  'Natural Environment',
  land_use:             'Land Use',
  buildings:            'Buildings & Structures',
  linear:               'Linear Infrastructure',
  water_infrastructure: 'Water Infrastructure',
  energy_utilities:     'Energy & Utilities',
  waste_environmental:  'Waste & Environmental',
};

/** All v2 category keys in display order */
export const ALL_CATEGORIES: InfraCategoryType[] = [
  'natural_environment', 'land_use', 'buildings', 'linear',
  'water_infrastructure', 'energy_utilities', 'waste_environmental',
];

/** Build IKU type string (v2 format) */
export function buildInfraIkuType(category: string, subcategory: string, type: string): string {
  return `iku:infrastructure:${category}:${subcategory}:${type}`;
}

export function normalizeInfraEntityType(category: string, subcategory: string, type: string): string {
  const trimmedType = String(type || '').trim();
  if (!trimmedType) return trimmedType;
  if (trimmedType.startsWith('iku:infrastructure:')) return trimmedType;
  return buildInfraIkuType(category, subcategory, trimmedType);
}

export function buildInfraTemplateId(category: string, subcategory: string, type: string): string {
  return `template:${buildInfraIkuType(category, subcategory, type)}`;
}

/** @deprecated — old industry colors kept for backward compat */
export const INDUSTRY_COLORS: Record<IndustryType, { bg: string; text: string; hex: string }> = {
  agriculture: { bg: 'bg-green-100',  text: 'text-green-700',  hex: '#22c55e' },
  mining:      { bg: 'bg-orange-100', text: 'text-orange-700', hex: '#f97316' },
  energy:      { bg: 'bg-yellow-100', text: 'text-yellow-700', hex: '#eab308' },
  logistics:   { bg: 'bg-indigo-100', text: 'text-indigo-700', hex: '#6366f1' },
  common:      { bg: 'bg-gray-100',   text: 'text-gray-700',   hex: '#6b7280' },
};

/** @deprecated — old industry icons kept for backward compat */
export const INDUSTRY_ICONS: Record<IndustryType, LucideIcon> = {
  agriculture: Tractor,
  mining: Mountain,
  energy: Zap,
  logistics: Truck,
  common: Building2,
};

/* ────────────────── Taxonomy API response type ────────────────── */

export interface TaxonomyCategory {
  label: string;
  description: string;
  color: string;
  icon: string;
  subcategories: Record<string, string[]>;
}

export interface TaxonomyTemplate {
  category: string;
  subcategory: string;
  geometry_type: FeatureType;
  industry_tags: string[];
  attributes: Record<string, { type: string; unit?: string; enum?: string[]; auto_fill_source?: string | null }>;
  template_id?: string;
  infrastructure_category?: string;
  infrastructure_type?: string;
  layer_key?: MapperLayerKey | string;
  land_categories?: LandCategory[];
  primary_land_category?: LandCategory | string;
  checklist?: {
    section_key: MapperLayerKey | string;
    section_label: string;
    prompt?: string;
    recommended?: boolean;
  };
  asset_mapping?: {
    asset_kind?: string;
    sync_mode?: string;
    source_of_truth?: string;
  };
  relationship_rules?: Array<{
    type: string;
    target_iku: string;
    cardinality?: string;
  }>;
}

export interface InfrastructureTaxonomy {
  version: string;
  categories: Record<string, TaxonomyCategory>;
  templates: Record<string, TaxonomyTemplate>;
  infrastructure_stream_mapping: Record<string, string[]>;
  environmental_streams: Record<string, { stream_id: string; description: string; measurements: string[] }>;
  migration_map: Record<string, string>;
  land_categories?: Record<LandCategory, { label: string; description: string }>;
  layer_definitions?: Record<MapperLayerKey | string, { key: string; label: string; description: string; style?: LayerStyle }>;
  selected_land_category?: LandCategory | null;
  checklist?: {
    land_category?: LandCategory | null;
    sections: Array<{
      key: string;
      label: string;
      description?: string;
      recommended?: boolean;
      templates: Array<{
        key: string;
        label: string;
        template_id?: string | null;
        geometry_type?: FeatureType;
        infrastructure_category?: string | null;
        subcategory?: string | null;
        layer_key?: string | null;
        land_categories?: string[];
        asset_mapping?: Record<string, unknown> | null;
        relationship_rules?: Array<Record<string, unknown>>;
      }>;
    }>;
  };
}

export interface LinkedApiEndpoint {
  api_id: string;
  api_name?: string | null;
  endpoint_id: string;
  provider?: string | null;
  method?: string | null;
  path?: string | null;
  template_id?: string | null;
  device_id?: string | null;
  linked_at?: string | null;
  linked_by?: string | null;
}

export interface WorkspaceApi {
  api_id: string;
  api_name?: string;
  provider?: string;
  status?: string;
  workspace?: {
    effective_group?: string | null;
    tags?: string[];
  };
  runtime?: {
    pipeline_count?: number;
    enabled_pipeline_count?: number;
    latest_ingest_at?: string | null;
  };
}

export interface WorkspaceApiEndpoint {
  endpoint_id: string;
  method?: string;
  path?: string;
  description?: string;
  entity_type?: string;
  template_id?: string;
}

export interface LayerStyle {
  color: string;
  fillOpacity: number;
  weight: number;
}

export interface MapLayer {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  layer_type: LayerType;
  visible: boolean;
  locked: boolean;
  style: LayerStyle;
  linked_entity_id: string | null;
  source: string;
  feature_count: number;
  bounds: [number, number, number, number] | null; // [west, south, east, north]
  metadata: {
    created_at: string;
    updated_at: string;
    created_by: string;
  };
}

export interface MapFeature {
  id: string;
  tenant_id: string;
  layer_id: string;
  name: string;
  feature_type: FeatureType;
  geometry: GeoJSON.Geometry;
  properties: Record<string, unknown>;
  linked_entity_id: string | null;
  linked_asset_id: string | null;
  source: string;
  style: Partial<LayerStyle> | null;
  status: FeatureStatus;
  area_m2: number;
  perimeter_m: number;
  metadata: {
    created_at: string;
    updated_at: string;
    created_by: string;
  };
}

/* ------------------------------------------------------------------ */
/*  API payloads                                                        */
/* ------------------------------------------------------------------ */

export interface CreateLayerPayload {
  name: string;
  description?: string;
  layer_type?: LayerType;
  style?: LayerStyle;
  linked_entity_id?: string;
}

export interface UpdateLayerPayload {
  name?: string;
  description?: string;
  layer_type?: LayerType;
  style?: LayerStyle;
  visible?: boolean;
  locked?: boolean;
  linked_entity_id?: string | null;
}

export interface CreateFeaturePayload {
  layer_id: string;
  name?: string;
  geometry: GeoJSON.Geometry;
  properties?: Record<string, unknown>;
  linked_entity_id?: string;
  linked_asset_id?: string;
  style?: Partial<LayerStyle>;
}

export interface UpdateFeaturePayload {
  name?: string;
  geometry?: GeoJSON.Geometry;
  properties?: Record<string, unknown>;
  linked_entity_id?: string | null;
  linked_asset_id?: string | null;
  style?: Partial<LayerStyle> | null;
  status?: FeatureStatus;
}

export interface ImportPreview {
  import_id: string;
  filename: string;
  feature_count: number;
  bounds: [number, number, number, number] | null;
  features: Array<{
    index: number;
    name: string;
    feature_type: FeatureType;
    geometry: GeoJSON.Geometry;
    properties: Record<string, unknown>;
    area_m2: number;
    perimeter_m: number;
  }>;
}

export interface CommitImportPayload {
  import_id: string;
  layer_id?: string;
  layer_name?: string;
}

export interface MapperStats {
  layers: number;
  features: number;
  total_area_m2: number;
  total_area_ha: number;
  by_feature_type: Record<string, number>;
}

export interface AgentMapPayload {
  entity_id?: string;
  parcel_id?: string;
  layer_id?: string;
  target_layer_id?: string;
  mode: 'extract_boundaries' | 'place_assets' | 'detect_infrastructure' | 'vision_detect';
  category?: InfraCategoryType;
  geometry?: GeoJSON.Geometry;
  scan_radius_m?: number;
  replace_existing?: boolean;
  image_url?: string;
  image_base64?: string;
  image_media_type?: string;
  declared_types?: string[];
  land_category?: LandCategory;
  /** @deprecated */ industry?: IndustryType;
}

export type GeeJobType =
  | 'ndvi_classification'
  | 'water_body_detection'
  | 'pivot_detection'
  | 'change_detection'
  | 'land_cover';

export type GeeJobStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface CreateGeeJobPayload {
  job_type: GeeJobType;
  parcel_id?: string;
  geometry?: GeoJSON.Geometry;
  params?: Record<string, unknown>;
}

export interface CreateGeeJobResult {
  success: boolean;
  job_id: string;
  status: GeeJobStatus | string;
  message?: string;
  tile_url?: string | null;
  result_infrastructure_ids?: string[];
  created_count?: number;
  result_summary?: Record<string, unknown> | null;
}

export interface MapperGeeJob {
  id: string;
  tenant_id: string;
  app_id: string | null;
  job_type: GeeJobType | string;
  parcel_id: string | null;
  geometry: GeoJSON.Geometry;
  params: Record<string, unknown>;
  status: GeeJobStatus | string;
  result_layer_id: string | null;
  result_infrastructure_ids: string[];
  tile_url?: string | null;
  error?: string | null;
  result_summary?: Record<string, unknown> | null;
  metadata: {
    created_at: string;
    updated_at: string;
    created_by: string;
  };
}

export interface MapRasterOverlay {
  id: string;
  kind: 'gee-ndvi';
  job_id: string | null;
  parcel_id: string | null;
  title: string;
  subtitle?: string | null;
  tile_url?: string | null;
  status: GeeJobStatus | string;
  error?: string | null;
  opacity: number;
}

/* ------------------------------------------------------------------ */
/*  Parcel (cadastral / land)                                           */
/* ------------------------------------------------------------------ */

export interface MapParcel {
  id: string;
  tenant_id: string;
  app_id?: string | null;
  feature_name?: string | null;
  external_id?: string | null;
  sg_number?: string | null;
  erf_number?: string | null;
  portion?: string | null;
  farm_name?: string | null;
  province?: string | null;
  municipality?: string | null;
  local_municipality?: string | null;
  district_municipality?: string | null;
  prcl_key?: string | null;
  land_category?: LandCategory | null;
  land_type?: string | null;
  country_code?: string | null;
  admin_level_1?: string | null;
  admin_level_2?: string | null;
  jurisdiction_type?: string | null;
  registry_type?: string | null;
  source_adapter?: string | null;
  source_tier?: string | null;
  source_pack_id?: string | null;
  natural_key?: string | null;
  natural_key_field?: string | null;
  natural_key_value?: string | null;
  local_identifiers?: Record<string, string | null>;
  geometry: GeoJSON.Geometry;
  area_ha: number;
  linked_entity_id: string | null;
  active_template_ids?: string[];
  template_id?: string | null;
  template_version?: string | null;
  promoted_to_pim?: boolean;
  pim_product_id?: string | null;
  farm_product_id?: string | null;
  status?: string | null;
  source_lineage?: {
    file?: string | null;
    checksum?: string | null;
    province?: string | null;
    country_code?: string | null;
    source_adapter?: string | null;
    source_pack_id?: string | null;
    source_tier?: string | null;
    decoder?: string | null;
    source_type?: string | null;
    record_index?: number | null;
    run_id?: string | null;
    ingested_at?: string | null;
    quarantined?: boolean;
    quarantine_reason?: string | null;
  };
  source: string;
  metadata: {
    created_at: string;
    updated_at: string;
    created_by: string;
    import_filename?: string;
  };
}

export interface RenderedParcelSummary {
  id: string;
  feature_name?: string | null;
  external_id?: string | null;
  sg_number?: string | null;
  erf_number?: string | null;
  farm_name?: string | null;
  province?: string | null;
  municipality?: string | null;
  local_municipality?: string | null;
  district_municipality?: string | null;
  prcl_key?: string | null;
  land_category?: LandCategory | null;
  land_type?: string | null;
  country_code?: string | null;
  admin_level_1?: string | null;
  admin_level_2?: string | null;
  natural_key_value?: string | null;
  area_ha: number;
  linked_entity_id: string | null;
}

export type MapperNavigatorTab = 'farms' | 'fields' | 'layers';

export interface MapperWorkspaceGroup {
  id: string;
  name: string;
  color?: string | null;
  created_at: string;
}

export interface MapperSavedFarm {
  id: string;
  feature_name?: string | null;
  external_id?: string | null;
  sg_number?: string | null;
  erf_number?: string | null;
  portion?: string | null;
  farm_name?: string | null;
  province?: string | null;
  municipality?: string | null;
  local_municipality?: string | null;
  district_municipality?: string | null;
  country_code?: string | null;
  admin_level_1?: string | null;
  admin_level_2?: string | null;
  registry_type?: string | null;
  source_adapter?: string | null;
  source_pack_id?: string | null;
  natural_key?: string | null;
  local_identifiers?: Record<string, string | null>;
  land_category?: LandCategory | null;
  land_type?: string | null;
  geometry: GeoJSON.Geometry;
  bounds: [number, number, number, number] | null;
  area_ha: number;
  linked_entity_id: string | null;
  workspace_group_id?: string | null;
  active_template_ids?: string[];
  source: string;
  saved_at: string;
  updated_at: string;
}

export interface MapLandUnit {
  id: string;
  name: string;
  kind?: string;
  iku_type?: string | null;
  source: 'derive' | 'draw' | 'mixed' | string;
  parcel_refs: string[];
  geometry: GeoJSON.Geometry | null;
  area_ha?: number | null;
  tags: string[];
  labels?: Record<string, string>;
  linked_entity_id?: string | null;
  provenance?: Record<string, unknown>;
  metadata?: { created_at: string; updated_at: string; created_by?: string };
}

export interface MapperWorkspaceViewState {
  bbox?: string;
  zoom?: number;
  show_boundaries?: boolean;
}

export interface MapperWorkspaceSnapshot {
  version: 1;
  saved_farms: MapperSavedFarm[];
  workspace_groups?: MapperWorkspaceGroup[];
  active_farm_id: string | null;
  active_farm_group_id?: string | null;
  active_entity_id: string | null;
  navigator_tab: MapperNavigatorTab;
  view?: MapperWorkspaceViewState;
  saved_at?: string | null;
}

export interface ParcelFilters {
  linked_entity_id?: string;
  province?: string;
  province_code?: string;
  sg_number?: string;
  prcl_key?: string;
  external_id?: string;
  country_code?: string;
  admin_level_1?: string;
  admin_level_2?: string;
  registry_type?: string;
  source_adapter?: string;
  source_pack_id?: string;
  natural_key?: string;
  natural_key_value?: string;
  identifier_key?: string;
  identifier_value?: string;
  search?: string;
  search_scope?: 'picker';
  erf_number?: string;
  land_category?: LandCategory;
  land_type?: string;
  bbox?: string;
  fields?: 'summary' | 'border';
  page?: number;
  limit?: number;
  skip_total?: boolean;
  enabled?: boolean;
}

export interface ParcelImportPreview {
  import_id: string;
  filename: string;
  parcel_count: number;
  parcels: Array<{
    index: number;
    feature_name?: string | null;
    external_id?: string | null;
    sg_number?: string | null;
    farm_name?: string | null;
    portion?: string | null;
    province?: string | null;
    country_code?: string | null;
    admin_level_1?: string | null;
    admin_level_2?: string | null;
    local_identifiers?: Record<string, string | null>;
    area_ha: number;
    geometry: GeoJSON.Geometry;
  }>;
}

/* ------------------------------------------------------------------ */
/*  Infrastructure object                                               */
/* ------------------------------------------------------------------ */

export interface MapInfrastructure {
  id: string;
  tenant_id: string;
  parcel_id: string | null;
  layer_id: string | null;
  farm_product_id?: string | null;
  name: string;
  infra_type: string;
  category: InfraCategoryType | string;
  subcategory: string | null;
  layer_key?: MapperLayerKey | string | null;
  land_categories?: string[];
  land_category?: LandCategory | null;
  industry_tags: string[];
  /** @deprecated — v1 field, prefer category */
  industry?: IndustryType | null;
  geometry: GeoJSON.Geometry;
  attributes: Record<string, unknown>;
  linked_entity_id: string | null;
  linked_device_ids: string[];
  linked_api_endpoints: LinkedApiEndpoint[];
  template_id?: string | null;
  checklist?: Record<string, unknown> | null;
  asset_mapping?: Record<string, unknown> | null;
  relationship_rules?: Array<Record<string, unknown>>;
  job_id?: string | null;
  suggestion_source_job_id?: string | null;
  source: string;
  status: FeatureStatus;
  area_m2: number;
  perimeter_m: number;
  metadata: {
    created_at: string;
    updated_at: string;
    created_by: string;
  };
}

export interface InfrastructureFilters {
  parcel_id?: string;
  infra_type?: string;
  category?: InfraCategoryType | string;
  subcategory?: string;
  layer_key?: MapperLayerKey | string;
  land_category?: LandCategory;
  job_id?: string;
  suggestion_source_job_id?: string;
  /** @deprecated */ industry?: IndustryType;
  linked_entity_id?: string;
  linked_device_id?: string;
  status?: FeatureStatus;
  bbox?: string;
  page?: number;
  limit?: number;
  enabled?: boolean;
}

export interface CreateInfrastructurePayload {
  parcel_id?: string;
  layer_id?: string;
  name: string;
  infra_type: string;
  category: string;
  subcategory?: string;
  land_category?: LandCategory;
  industry_tags?: string[];
  template_id?: string;
  geometry: GeoJSON.Geometry;
  attributes?: Record<string, unknown>;
  linked_entity_id?: string;
}

export interface UpdateInfrastructurePayload {
  name?: string;
  infra_type?: string;
  category?: string;
  subcategory?: string;
  land_category?: LandCategory | null;
  industry_tags?: string[];
  geometry?: GeoJSON.Geometry;
  attributes?: Record<string, unknown>;
  parcel_id?: string | null;
  layer_id?: string | null;
  linked_entity_id?: string | null;
  status?: FeatureStatus;
  template_id?: string | null;
}

export interface MapperSharedParcelGroup {
  id: string;
  name: string;
  color?: string | null;
  linked_entity_id?: string | null;
  parcel_ids: string[];
  source?: string;
  metadata: {
    created_at: string;
    updated_at: string;
    created_by: string;
  };
}

export interface TemplateAttributeSchemaEntry {
  key: string;
  name?: string;
  type: string;
  unit?: string;
  enum?: string[];
  default_value?: unknown;
  auto_fill_source?: string | null;
}

export interface ArmedInfrastructureTemplate {
  template_id: string;
  name: string;
  category: InfraCategoryType;
  subcategory: string;
  type: string;
  geometry_type: FeatureType;
  industry_tags: string[];
  attribute_schema: TemplateAttributeSchemaEntry[];
  layer_key?: MapperLayerKey | string | null;
  checklist?: Record<string, unknown> | null;
  land_categories?: string[];
}

export interface MapperBookmark {
  id: string;
  name: string;
  parcel_id?: string | null;
  group_id?: string | null;
  linked_entity_id?: string | null;
  land_category?: LandCategory | null;
  view?: MapperWorkspaceViewState;
  filters?: Record<string, unknown>;
  source?: string;
  metadata: {
    created_at: string;
    updated_at: string;
    created_by: string;
  };
}

export interface MapperSuggestionMetrics {
  review?: number;
  active?: number;
  rejected?: number;
  [key: string]: number | undefined;
}

export interface TwinState {
  infrastructure: MapInfrastructure;
  operational_asset?: Record<string, unknown> | null;
  parcel: MapParcel | null;
  parcel_twin?: ParcelTwinState | null;
  entity: { id: string; name: string; entity_type: string; status?: string } | null;
  devices: Array<{
    device: { deviceId: string; type: string; status: string };
    latest_telemetry: Record<string, unknown> | null;
    health: { status: string; lastHeartbeatAt: string | null } | null;
  }>;
  geometry?: GeoJSON.Geometry;
  attributes?: Record<string, unknown>;
  streams?: Array<{ stream_id: string; description?: string; measurements?: string[] }>;
  api_links?: LinkedApiEndpoint[];
  relationships?: Array<{ source: string; target: string; type: string; metadata?: Record<string, unknown> }>;
  ndvi?: { job_id: string; status: string; tile_url?: string | null } | null;
  weather?: { linked_feed_count: number; feeds: LinkedApiEndpoint[]; available: boolean } | null;
}

export interface ParcelTwinState {
  parcel: MapParcel & {
    farm_product_id?: string | null;
  };
  entity: { id: string; name: string; entity_type: string; status?: string } | null;
  fields: MapInfrastructure[];
  infrastructure: MapInfrastructure[];
  relationships: Array<{ source: string; target: string; type: string; metadata?: Record<string, unknown> }>;
  ndvi: { job_id: string; status: string; tile_url?: string | null; created_at?: string | null } | null;
  weather: { linked_feed_count: number; feeds: LinkedApiEndpoint[]; available: boolean };
  telemetry: { linked_device_ids: string[]; linked_device_count: number };
}

/* ------------------------------------------------------------------ */
/*  Filters                                                             */
/* ------------------------------------------------------------------ */

export interface LayerFilters {
  linked_entity_id?: string;
  layer_type?: LayerType;
  page?: number;
  limit?: number;
  enabled?: boolean;
}

export interface FeatureFilters {
  layer_id?: string;
  linked_entity_id?: string;
  linked_asset_id?: string;
  status?: FeatureStatus;
  bbox?: string;
  page?: number;
  limit?: number;
  enabled?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Draw mode                                                           */
/* ------------------------------------------------------------------ */

export type DrawMode = 'none' | 'point' | 'polygon' | 'line' | 'circle';

/* ------------------------------------------------------------------ */
/*  Visual constants                                                    */
/* ------------------------------------------------------------------ */

export const LAYER_TYPE_ICONS: Record<string, LucideIcon> = {
  boundary: Pentagon,
  infrastructure: Building2,
  parcel: Landmark,
  import: Upload,
  agent: Sparkles,
  custom: Layers,
};

export const LAYER_TYPE_COLORS: Record<string, { bg: string; text: string; hex: string }> = {
  boundary:       { bg: 'bg-blue-100',    text: 'text-blue-700',    hex: '#3b82f6' },
  infrastructure: { bg: 'bg-amber-100',   text: 'text-amber-700',   hex: '#f59e0b' },
  parcel:         { bg: 'bg-teal-100',    text: 'text-teal-700',    hex: '#14b8a6' },
  import:         { bg: 'bg-purple-100',  text: 'text-purple-700',  hex: '#8b5cf6' },
  agent:          { bg: 'bg-emerald-100', text: 'text-emerald-700', hex: '#10b981' },
  custom:         { bg: 'bg-slate-100',   text: 'text-slate-700',   hex: '#64748b' },
};

export const FEATURE_STATUS_COLORS: Record<string, { dot: string; bg: string; text: string }> = {
  active: { dot: 'bg-green-500',  bg: 'bg-green-100',  text: 'text-green-700' },
  draft:  { dot: 'bg-yellow-500', bg: 'bg-yellow-100', text: 'text-yellow-700' },
  review: { dot: 'bg-orange-500', bg: 'bg-orange-100', text: 'text-orange-700' },
};

export const DEFAULT_LAYER_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

export const DRAW_MODE_LABELS: Record<DrawMode, string> = {
  none: 'Select',
  point: 'Marker',
  polygon: 'Polygon',
  line: 'Line',
  circle: 'Circle',
};

export const DRAW_MODE_ICONS: Record<DrawMode, LucideIcon> = {
  none: Map,
  point: MapPin,
  polygon: Pentagon,
  line: Route,
  circle: CircleDot,
};
